import { HttpErrorResponse } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoginService } from '../../login/login.service';
import { LINKS } from '../../../../../../../libs/common-utils/src/lib/constants/links';

@Component({
    selector: 'homeleite-first-access-dialog',
    templateUrl: './first-access-dialog.component.html',
    styleUrls: ['./first-access-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirstAccessDialogComponent implements OnInit {
    form: FormGroup;
    isLoading: boolean;
    private key: string;

    constructor(
        private loginService: LoginService,
        private snackbar: MatSnackBar,
        private formBuilder: FormBuilder,
        private changeDetector: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.createForm();
    }

    private createForm(): void {
        this.form = this.formBuilder.group({
            identification: this.formBuilder.group({
                identificationNumber: ['', Validators.required],
                apcId: ['', Validators.required]
            }),
            registration: this.formBuilder.group({
                email: ['', [Validators.required, Validators.email]],
                password: [
                    '',
                    [
                        Validators.required,
                        Validators.minLength(5),
                        Validators.maxLength(30)
                    ]
                ]
            })
        });
    }

    get title(): string {
        return 'Primeiro Acesso';
    }

    get identification(): FormGroup {
        return this.form.get('identification') as FormGroup;
    }

    get identificationNumberRequired(): boolean {
        return this.identification.get('identificationNumber')?.hasError('required');
    }

    get apcIdRequired(): boolean {
        return this.identification.get('apcId')?.hasError('required');
    }

    get registration(): FormGroup {
        return this.form.get('registration') as FormGroup;
    }

    get emailErrorType(): 'required' | 'email' | null {
        if (this.registration.get('email')?.hasError('required')) {
            return 'required';
        }
        else if (this.registration.get('email')?.hasError('email')) {
            return 'email';
        }
        return null;
    }

    get passwordErrorType(): 'required' | 'minlength' | 'maxlength' | null {
        if (this.registration.get('password')?.hasError('required')) {
            return 'required';
        } else if (this.registration.get('password')?.hasError('minlength')) {
            return 'minlength';
        } else if (this.registration.get('password')?.hasError('maxlength')) {
            return 'maxlength';
        }
        return null;
    }

    async requestKey(): Promise<void> {
        if (this.identification.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.isLoading = true;

        try {
            const { identificationNumber, apcId } = this.identification.getRawValue();
            this.key = await this.loginService.handleFirstAccess(identificationNumber, apcId);
        } catch (error: any) {
            if (error?.status === 409) {
                const message =
                    'Seu cadastro possui pendências. Para resolver, clique no botão ao lado e fale com o setor financeiro.';

                this.showSnackbarMessage(message, {
                    text: 'FALAR NO WHATSAPP',
                    callback: () => {
                        window.open(LINKS.WHATSAPP_FINANCE_SUPPORT, '_blank');
                    }
                });
            } else {
                this.showSnackbarMessage(
                    'Dados incorretos. Por favor, verifique o que foi digitado e tente novamente.'
                );
            }
        }

        this.isLoading = false;
        this.identification.reset();
        this.changeDetector.markForCheck();
    }

    async createUser(): Promise<void> {
        if (this.registration.invalid) {
            this.registration.markAllAsTouched();
            return;
        }

        this.isLoading = true;

        try {
            const { email, password } = this.registration.getRawValue();

            await this.loginService.setPasswordAndEmail(this.key, email, password);
            await this.loginService.authenticateAndInitiateSync(email, password);

            this.showSnackbarMessage('Usuário cadastrado com sucesso!');
            this.identification.reset();
        } catch (error) {
            if (error instanceof HttpErrorResponse) {
                let errorMessage = 'Ocorreu um erro ao processar sua solicitação.';
                const httpError = error.error
                if (httpError?.message) {
                    errorMessage = httpError.message;
                } else if (Array.isArray(httpError) && httpError[0]?.message) {
                    errorMessage = httpError[0].message;
                }
                this.showSnackbarMessage(errorMessage);
            } else {
                console.error('Erro inesperado:', error);
                this.showSnackbarMessage('Não foi possível conectar ao servidor. Tente novamente.');
            }
        } finally {
            this.isLoading = false;
            this.changeDetector.markForCheck();
        }
    }

    private showSnackbarMessage(
        message: string,
        actionConfig?: { text: string; callback: () => void }
    ): void {
        const actionText = actionConfig ? actionConfig.text : 'Fechar';

        const snackBarRef = this.snackbar.open(message, actionText, {
            duration: 15000,
            panelClass: ['custom-snackbar'],
            verticalPosition: 'top'
        });

        if (actionConfig?.callback) {
            snackBarRef.onAction().subscribe(() => {
                actionConfig.callback();
            });
        }
    }
}
